import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Search,
  Users,
  Mail,
  Shield,
  ShieldCheck,
  Eye,
  EyeOff
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from './ui/table';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [userDialog, setUserDialog] = useState({ 
    open: false, 
    action: 'create',
    user: null
  });
  
  const [deleteDialog, setDeleteDialog] = useState({ 
    open: false, 
    user: null 
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    profile_id: '', // Único campo de permissão - baseado no perfil
    employee_id: '', // Novo campo para vinculação com funcionário
    is_active: true,
    force_password_reset: false
  });

  // User profiles
  const [userProfiles, setUserProfiles] = useState([]);
  
  // Employees list for dropdown
  const [employees, setEmployees] = useState([]);

  const [showPassword, setShowPassword] = useState(false);

  const countPermissions = (user) => {
    const profile = userProfiles.find((p) => p.id === user.profile_id);
    if (!profile) return user.menu_permissions?.length || 0;
    if (profile.permitted_ids?.length > 0) return profile.permitted_ids.length;
    if (!profile.granular_permissions || profile.granular_permissions.length === 0) {
      return profile.menu_permissions?.length || 0;
    }
    let count = 0;
    profile.granular_permissions.forEach((module) => {
      if (module.enabled && module.tabs && module.tabs.length > 0) {
        module.tabs.forEach((tab) => {
          if (tab.enabled) {
            count++;
            if (tab.page_tabs && tab.page_tabs.length > 0) {
              count += tab.page_tabs.length;
            }
          }
        });
      } else if (module.enabled) {
        count++;
      }
    });
    return count;
  };

  useEffect(() => {
    fetchUsers();
    fetchUserProfiles();
    fetchEmployees();
  }, []);

  const fetchUserProfiles = async () => {
    try {
      const response = await axios.get(`${API}/user-profiles`);
      setUserProfiles(response.data);
    } catch (error) {
      console.error('Erro ao carregar perfis de usuário:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
      toast.error('Erro ao carregar lista de funcionários');
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar lista de usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      if (!formData.name || !formData.email || !formData.password) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }

      if (!formData.profile_id) {
        toast.error('Selecione um perfil para o usuário');
        return;
      }

      if (!formData.employee_id) {
        toast.error('Selecione um funcionário para vincular ao usuário');
        return;
      }

      await axios.post(`${API}/users`, formData);
      toast.success('Usuário criado com sucesso!');
      fetchUsers();
      closeUserDialog();
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const handleUpdateUser = async () => {
    try {
      if (!formData.name || !formData.email) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }

      if (!formData.profile_id) {
        toast.error('Selecione um perfil para o usuário');
        return;
      }

      if (!formData.employee_id) {
        toast.error('Selecione um funcionário para vincular ao usuário');
        return;
      }

      const updateData = {
        name: formData.name,
        email: formData.email,
        profile_id: formData.profile_id,
        employee_id: formData.employee_id,
        is_active: formData.is_active,
        force_password_reset: formData.force_password_reset
      };

      await axios.put(`${API}/users/${userDialog.user.id}`, updateData);
      toast.success('Usuário atualizado com sucesso!');
      fetchUsers();
      closeUserDialog();
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar usuário');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/users/${deleteDialog.user.id}`);
      toast.success('Usuário removido com sucesso!');
      fetchUsers();
      setDeleteDialog({ open: false, user: null });
    } catch (error) {
      console.error('Erro ao remover usuário:', error);
      toast.error(error.response?.data?.detail || 'Erro ao remover usuário');
    }
  };

  const openUserDialog = (action, user = null) => {
    setUserDialog({ open: true, action, user });
    
    if (action === 'edit' && user) {
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        profile_id: user.profile_id || '',
        employee_id: user.employee_id || '',
        is_active: user.is_active,
        force_password_reset: user.force_password_reset || false
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        profile_id: '',
        employee_id: '',
        is_active: true,
        force_password_reset: false
      });
    }
    setShowPassword(false);
  };

  const closeUserDialog = () => {
    setUserDialog({ open: false, action: 'create', user: null });
    setFormData({
      name: '',
      email: '',
      password: '',
      profile_id: '',
      employee_id: '',
      is_active: true,
      force_password_reset: false
    });
  };


  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Gerenciar Usuários</h1>
          <p className="text-slate-600 mt-1">
            Gerencie usuários e suas permissões no sistema
          </p>
        </div>
        <Button
          onClick={() => openUserDialog('create')}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Novo Usuário</span>
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                placeholder="Buscar por nome ou e-mail..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Badge variant="outline" className="flex items-center space-x-2">
              <Users className="h-3 w-3" />
              <span>{filteredUsers.length} usuários</span>
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Users table */}
      <Card className="table-container">
        <CardHeader>
          <CardTitle>Lista de Usuários</CardTitle>
          <CardDescription>
            Visualize e gerencie todos os usuários do sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Funcionário</TableHead>
                  <TableHead>Perfil</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Permissões</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                      {searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} className="table-row">
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {user.employee_name ? (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                            {user.employee_name}
                          </Badge>
                        ) : (
                          <span className="text-gray-400 text-xs">Não vinculado</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.profile_name ? (
                          <Badge variant="outline" className="text-xs">
                            {user.profile_name}
                          </Badge>
                        ) : (
                          <span className="text-gray-400 text-xs">Individual</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.is_admin ? 'default' : 'secondary'}>
                          {user.is_admin ? (
                            <>
                              <ShieldCheck className="h-3 w-3 mr-1" />
                              Administrador
                            </>
                          ) : (
                            <>
                              <Shield className="h-3 w-3 mr-1" />
                              Usuário
                            </>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          <Badge variant="outline" className="text-xs">
                            {countPermissions(user)} telas
                          </Badge>
                          {user.can_manage_signatures && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                              Assinaturas
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.is_active ? 'default' : 'secondary'}>
                          {user.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(user.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openUserDialog('edit', user)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {user.id !== currentUser.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteDialog({ open: true, user })}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit User Dialog */}
      <Dialog open={userDialog.open} onOpenChange={(open) => !open && closeUserDialog()}>
        <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {userDialog.action === 'create' ? 'Novo Usuário' : 'Editar Usuário'}
            </DialogTitle>
            <DialogDescription>
              {userDialog.action === 'create' 
                ? 'Preencha os dados do novo usuário'
                : 'Edite as informações do usuário'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nome completo"
              />
            </div>
            
            <div>
              <Label htmlFor="email">E-mail *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@exemplo.com"
              />
            </div>

            {/* Seleção de Funcionário */}
            <div>
              <Label htmlFor="employee">Funcionário *</Label>
              <select
                id="employee"
                value={formData.employee_id}
                onChange={(e) => setFormData(prev => ({ ...prev, employee_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Selecione um funcionário</option>
                {employees.map(employee => (
                  <option key={employee.id} value={employee.id}>
                    {employee.nome}
                  </option>
                ))}
              </select>
              <p className="text-sm text-gray-500 mt-1">
                Todo usuário deve estar vinculado a um funcionário cadastrado
              </p>
            </div>
            
            {/* Seleção de Perfil de Usuário */}
            <div>
              <Label htmlFor="profile">Perfil de Usuário *</Label>
              <select
                id="profile"
                value={formData.profile_id}
                onChange={(e) => setFormData(prev => ({ ...prev, profile_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Selecione um perfil</option>
                {userProfiles.map(profile => (
                  <option key={profile.id} value={profile.id}>
                    {profile.name} {profile.is_admin ? '(Admin)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-sm text-gray-500 mt-1">
                O perfil selecionado definirá todas as permissões do usuário
              </p>
            </div>
            
            {userDialog.action === 'create' && (
              <div>
                <Label htmlFor="password">Senha *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Senha do usuário"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}
            
            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="is_active">Usuário ativo</Label>
            </div>

            {userDialog.action === 'edit' && (
              <div className="flex items-center space-x-2 pt-2 border-t border-slate-200">
                <Switch
                  id="force_password_reset"
                  checked={formData.force_password_reset}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, force_password_reset: checked }))}
                />
                <Label htmlFor="force_password_reset" className="text-amber-600 font-medium">
                  Redefinir senha no próximo login
                </Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeUserDialog}
            >
              Cancelar
            </Button>
            <Button
              onClick={userDialog.action === 'create' ? handleCreateUser : handleUpdateUser}
              className="btn-primary"
            >
              {userDialog.action === 'create' ? 'Criar' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover o usuário <strong>{deleteDialog.user?.name}</strong>?
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, user: null })}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              className="btn-danger"
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;